import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { hooks as h } from "@/hooks";
import { components as c } from "@/components";
import Stepper from "@/components/common/Stepper";
import { hasTeamInviteContext, validateTeamInviteSearch } from "@/features/invite/utils/teamInviteSearch";
import { getTeamInviteSteps } from "@/features/invite/utils/teamInviteSteps";
import QRCodeRoute from "@/features/qr-code/components/QrCodeRoute";
import { BackButton } from "@/features/i18n/formComponents";
import { useActionCopy } from "@/features/i18n/actionCopy";
import { useInviteCopy } from "@/features/i18n/inviteCopy";
import { formatRoleLabel, useProfileCopy } from "@/features/i18n/profileCopy";
import { useAuth } from "@/utils/auth";
import { useProfileByIdQuery } from "@/features/profile/api/selectProfile";
import type { RoleT } from "@patient-watch/types";

export const Route = createFileRoute("/_app/team/invite/qr")({
    component: TeamInviteQrPage,
    validateSearch: validateTeamInviteSearch,
    beforeLoad: ({ search }) => {
        if (!hasTeamInviteContext(search)) {
            throw redirect({ to: "/team/invite" });
        }
    },
});

function TeamInviteQrPage() {
    const search = Route.useSearch();
    const navigate = useNavigate();
    const auth = useAuth();
    const { data: profile } = useProfileByIdQuery(auth.id);
    const copy = useProfileCopy();
    const inviteCopy = useInviteCopy();
    const actionCopy = useActionCopy();
    const role = (search.role ?? "").toUpperCase() as RoleT;
    const roleLabel = formatRoleLabel(role, copy) || search.role;

    h.useNav([
        { label: copy.myTeam, route: { to: "/team" } },
        { label: inviteCopy.invitePageTitle, route: { to: "/team/invite", search } },
        { label: inviteCopy.shareQrCode, route: { to: "/team/invite/qr", search } },
    ]);

    return (
        <>
            <c.cc.Title words={inviteCopy.shareQrCode} />
            {search.organisation_name || roleLabel ? (
                <p className="text-muted-foreground text-sm">
                    {[roleLabel, search.organisation_name].filter(Boolean).join(" · ")}
                </p>
            ) : null}
            <Stepper steps={getTeamInviteSteps("qr", inviteCopy, search)} />
            <QRCodeRoute
                route={{
                    to: "/signup",
                    search: {
                        role,
                        organisation_id: search.organisation_id,
                        organisation: {
                            organisation_id: search.organisation_id,
                            organisation_name: search.organisation_name ?? "",
                        },
                        invited_by: auth.id,
                        invited_by_formal_name: profile?.formal_name ?? undefined,
                    },
                }}
                title={`Signup as a ${roleLabel?.toLowerCase() ?? "user"} in ${search.organisation_name ?? "your organisation"}`}
            />
            <BackButton
                className="w-full"
                label={actionCopy.back}
                onClick={() => void navigate({ to: "/team/invite", search })}
            />
        </>
    );
}
